/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/objects/[kind]/_detail-client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`                                    -> dropped (inert here)
 *   `import type { ObjectTypeRow, CustomFieldDefRow }`
 *     from `@excellent/core/server`                   -> the contract's
 *                                                        `ObjectTypeRowDto` /
 *                                                        `ObjectTypeFieldDto`
 *   the local `ObjectRecordRow` interface              -> the contract's
 *                                                        `ObjectRecordRowDto`
 *
 * The type swap is not cosmetic. Those were `import type` — erased at build, so
 * they never bundled the vault and `check-server-leak` was right not to flag them
 * — but they typed the props against a SERVER row shape that this host can no
 * longer produce. The contract now owns the shape, zod-validated at the boundary,
 * and the three declarations collapse to one. The fields are the same fields.
 *
 * `fields` narrowed as a side effect and that is deliberate: the endpoint returns
 * the ten `CustomFieldDefRow` columns this component actually renders, not all
 * fifteen. `workspace_id` and `product` were never read here, and shipping them to
 * a browser would be volunteering tenancy detail for nothing.
 */

import { useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  DataTable,
  DodPredicatesTable,
  IntentBadge,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  formatDate,
  formatDateTime,
  type DataTableColumn,
  type Intent,
} from "@excellent/ui";
import type {
  ObjectTypeRowDto,
  ObjectTypeFieldDto,
  ObjectRecordRowDto,
} from "@excellent/ui/api/contracts/database/object-types";

const ORIGIN_INTENT: Record<ObjectTypeRowDto["origin"], Intent> = {
  system: "neutral",
  user: "info",
  agent: "success",
};

/**
 * Object-type detail body (PRD §3.4) — in-card `Tabs` (the local-state atom, the
 * correct grammar for panel switching):
 *   • Fields — read-only list-row over custom_field_defs (write helpers land with
 *     custom-fields CRUD, #184; the "Add field" button is disabled with a tooltip).
 *   • Records — a DataTable of the kind's rows; commitment-backed rows link to
 *     their /o/<uid> object page.
 *   • About — read-only display of the definition (label/plural/description/state
 *     machine). Inline-edit deferred: v1 ships the read-only display, which the
 *     task permits ("read-only display fine for v1 if inline-edit is heavy").
 */
export function ObjectTypeDetailClient({
  type,
  fields,
  records,
}: {
  type: ObjectTypeRowDto;
  fields: ObjectTypeFieldDto[];
  records: ObjectRecordRowDto[];
}) {
  const states = useMemo(() => parseStates(type.state_machine), [type.state_machine]);

  const recordColumns = useMemo<DataTableColumn<ObjectRecordRowDto>[]>(() => {
    const cols: DataTableColumn<ObjectRecordRowDto>[] = [
      {
        key: "name",
        header: type.storage === "commitment" ? "Title" : "Name",
        render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
      },
    ];
    if (type.storage === "commitment") {
      cols.push({
        key: "state",
        header: "State",
        render: (r) =>
          r.state ? (
            <Badge variant="neutral">{r.state.replace(/_/g, " ")}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      });
    }
    cols.push({
      key: "updated",
      header: "Updated",
      render: (r) => (
        <span className="text-muted-foreground">
          {r.updated ? formatDate(r.updated) : "—"}
        </span>
      ),
    });
    return cols;
  }, [type.storage]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={type.label}
        description={type.description ?? undefined}
        toolbar={
          <IntentBadge intent={ORIGIN_INTENT[type.origin]}>
            {type.origin === "system" ? "System" : type.origin === "user" ? "User" : "Agent"}
          </IntentBadge>
        }
      />

      <Tabs defaultValue="fields" className="gap-4">
        <TabsList>
          <TabsTrigger value="fields">Fields</TabsTrigger>
          <TabsTrigger value="records">Records</TabsTrigger>
          <TabsTrigger value="predicates">Predicates</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
        </TabsList>

        {/* Fields — read-only list-row over custom_field_defs (v1). */}
        <TabsContent value="fields">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="type-body font-medium text-foreground">Fields</span>
                <span className="type-subtext text-muted-foreground">
                  Custom fields defined for this object.
                </span>
              </div>
              <Button
                variant="ghost"
                disabled
                title="lands with custom-fields CRUD (#184)"
              >
                Add field
              </Button>
            </div>
            {fields.length === 0 ? (
              <p className="py-6 type-body text-muted-foreground">
                No fields defined yet.
              </p>
            ) : (
              <div className="flex flex-col">
                {fields.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between border-b border-border/40 py-2.5 first:pt-0 last:border-b-0"
                  >
                    <div className="flex flex-col">
                      <span className="type-body text-foreground">{f.name}</span>
                      <span className="font-mono type-subtext text-muted-foreground">{f.slug}</span>
                    </div>
                    <Badge variant="neutral">{f.value_type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="predicates">
          <DodPredicatesTable entityKind={type.kind} />
        </TabsContent>

        {/* Records — the kind's rows on its declared store. */}
        <TabsContent value="records">
          <DataTable
            rows={records}
            columns={recordColumns}
            rowKey={(r) => r.id}
            rowHref={records.some((r) => r.href) ? (r) => r.href ?? "" : undefined}
            title={type.label_plural ?? `${type.label} records`}
            count={records.length}
            empty="No records of this kind yet."
          />
        </TabsContent>

        {/* About — read-only definition display (v1). */}
        <TabsContent value="about">
          <Card className="p-6">
            <div className="flex flex-col">
              <AboutRow label="Label" value={type.label} />
              <AboutRow label="Plural" value={type.label_plural ?? "—"} />
              <AboutRow label="Kind slug" value={type.kind} mono />
              <AboutRow
                label="Storage"
                value={
                  type.storage === "commitment"
                    ? "Lifecycle record (commitment)"
                    : `Party overlay (${type.party_base})`
                }
              />
              <AboutRow label="Description" value={type.description ?? "—"} />
              {type.storage === "commitment" ? (
                <AboutRow
                  label="Lifecycle"
                  value={states.length > 0 ? states.join(" → ") : "—"}
                />
              ) : null}
              <AboutRow label="Created" value={formatDateTime(type.created_at)} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AboutRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-4 border-b border-border/40 py-2.5 first:pt-0 last:border-b-0">
      <span className="w-28 shrink-0 type-subtext text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono type-body text-foreground" : "type-body text-foreground"}>
        {value}
      </span>
    </div>
  );
}

/** Parse the {states,terminal} JSON blob into an ordered state list. */
function parseStates(stateMachine: string | null): string[] {
  if (!stateMachine) return [];
  try {
    const parsed = JSON.parse(stateMachine) as { states?: string[] };
    return Array.isArray(parsed.states) ? parsed.states : [];
  } catch {
    return [];
  }
}
